/**
 * Error monitoring with Bugsnag. Only enabled when the build has an API key (`BUGSNAG_API_KEY` at build time), so
 * local and CI builds report nothing.
 */
import Bugsnag from '@bugsnag/js';

import {getEnvironment, getPolyshoppenUser, getShippingStation, isLoggedIn, subscribe} from './session';

const apiKey = __BUGSNAG_API_KEY__;

let started = false;

/** the part of a Bugsnag event `isStaleChunkSyntaxError` looks at */
type ReportedError = {errorClass: string; errorMessage: string; stacktrace: {file?: string}[]};

/**
 * A chunk of this build that a deploy removed: App Platform answers with index.html (200, text/html), which throws a
 * SyntaxError on its `<` when it runs as a script. The ChunkLoadError that follows reloads the page (see
 * `app-update.ts`), so this one is noise. Only for the app's own chunks, the `.js` files at the root of the origin the
 * assets are served from (the public path is `/`): a SyntaxError in any other file is reported.
 */
const isStaleChunkSyntaxError = (error: ReportedError | undefined): boolean => {
    const file = error?.stacktrace[0]?.file;

    if ('SyntaxError' !== error?.errorClass || !error.errorMessage.includes("'<'") || !file) {
        return false;
    }

    try {
        const url = new URL(file);

        return window.location.origin === url.origin && /^\/[\w.-]+\.js$/.test(url.pathname);
    } catch {
        return false;
    }
};

export const startErrorReporting = (): void => {
    if (!apiKey || started) {
        return;
    }

    Bugsnag.start({
        apiKey,
        // the build id, as reported to Bugsnag's Build API with its commit (`scripts/report-build.mjs`): that links the
        // stack traces to the code on GitHub
        appVersion: __BUILD_ID__,
        // sessions are started where a user starts working (see `startReportingSession`), not on every page load: a login page
        // nobody uses yet is no session, and a start-up followed by a login no two
        autoTrackSessions: false,
        onError: event => {
            if (isStaleChunkSyntaxError(event.errors[0])) {
                return false;
            }

            const user = getPolyshoppenUser();

            if (user) {
                event.setUser(String(user.id), undefined, user.name);
            }

            event.addMetadata('terminal', {
                // the polyestershoppen.nl API the terminal talks to, chosen in the terminal itself
                environment: getEnvironment(),
                shippingStation: getShippingStation()?.shippingStation ?? null,
                printerId: getShippingStation()?.printerId ?? null,
                online: navigator.onLine,
            });
        },
    });

    started = true;

    // a reload (e.g. into a new deploy) with a user still logged in: another run of the app, on this build
    if (isLoggedIn()) {
        Bugsnag.startSession();
    }

    // the session ended (a logout, here or in another tab): errors on the login page are not the user's session's
    let user = getPolyshoppenUser();

    subscribe(() => {
        const previous = user;

        user = getPolyshoppenUser();

        if (previous && !user) {
            Bugsnag.pauseSession();
        }
    });
};

/**
 * A user starts working: a new Bugsnag session, which counts towards the stability of this build. For a login only; an
 * unlock after the idle lock is the same user carrying on, in the same session.
 */
export const startReportingSession = (): void => {
    if (started) {
        Bugsnag.startSession();
    }
};

/**
 * Note something that explains the next error that is reported (if any): what the operator did (`user`), a request
 * (`request`), or a change of the terminal's state (`state`).
 */
export const leaveBreadcrumb = (
    message: string,
    metadata?: Record<string, string | number | boolean | null | undefined>,
    type: 'state' | 'user' | 'request' = 'state',
): void => {
    if (started) {
        Bugsnag.leaveBreadcrumb(message, metadata, type);
    }
};

/**
 * Report an error that was handled (e.g. caught by an error boundary), with extra metadata if given. `groupingHash`
 * groups the events by it instead of by the error's class and stack trace, e.g. a failed request by its action and its
 * error code. Without Bugsnag (local and CI builds) it goes to the console instead.
 */
export const reportError = (
    error: unknown,
    metadata?: {section: string; values: Record<string, unknown>},
    severity: 'error' | 'warning' | 'info' = 'error',
    groupingHash?: string,
): void => {
    if (!started) {
        const log = 'error' === severity ? console.error : console.warn;

        log('[not reported]', error, metadata?.values ?? '');
        return;
    }

    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), event => {
        event.severity = severity;

        if (groupingHash) {
            event.groupingHash = groupingHash;
        }

        if (metadata) {
            event.addMetadata(metadata.section, metadata.values);
        }
    });
};
